import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import useBackendClient from '~/api/BackendClient/useBackendClient'
import type {
  WellPage_PastObservation,
  WellPageResponse,
} from '~/api/backend/pages/wellPageAPI'
import { QUERY_KEYS } from '~/api/query-client-keys'
import { warmImageURL } from '~/utils/images'
import { prefersLargeMontageSprite } from '../plateSpriteSizing'
import { LateralNavDirection } from './LateralNavCompass'

/**
 * Helper function to preload a single sprite URL.
 * @returns true if the sprite was fetched successfully, false otherwise.
 */
async function preloadSingleSpriteUrl(spriteUrl: string): Promise<boolean> {
  try {
    const warmedUrl = warmImageURL(spriteUrl)
    const response = await fetch(warmedUrl, { method: 'GET', cache: 'force-cache' })
    return response.ok
  } catch {
    // Network error - silently fail
    return false
  }
}

/**
 * Helper function to preload the server-stitched sprite image for an observation.
 * This is the highest priority image to preload since it enables instant rendering.
 *
 * Strategy: Preload v2 sprite based on display resolution, fall back to v1.
 *
 * @returns true only when a sprite was fetched successfully (response.ok);
 *          false for 404/403 (sprite not backfilled yet), other HTTP errors, or network failures.
 */
async function preloadSpriteImage(
  observation: WellPage_PastObservation,
): Promise<boolean> {
  if (observation.type !== 'plate_imaging' || !observation.montage) return false

  // Select v2 sprite URL based on display resolution (matching MontageHero zoom behavior)
  // Use zoomEnabled: true so the preloaded sprite matches the larger asset requested during pan/zoom
  const spriteUrlV2 = prefersLargeMontageSprite({ zoomEnabled: true })
    ? (observation.montage.stitchedImageUrlLarge ??
      observation.montage.stitchedImageUrlStandard)
    : observation.montage.stitchedImageUrlStandard

  if (spriteUrlV2) {
    const v2Success = await preloadSingleSpriteUrl(spriteUrlV2)
    if (v2Success) return true
  }

  // Fall back to v1 sprite
  const spriteUrlV1 = observation.montage.stitchedImageUrl
  if (spriteUrlV1) {
    return await preloadSingleSpriteUrl(spriteUrlV1)
  }

  return false
}

/**
 * Helper function to preload images for a single observation.
 * Warms browser HTTP cache for images used in rendering.
 *
 * Strategy: Preload sprite AND large images in parallel to handle partial backfill cases
 * where the API returns a sprite URL but the file doesn't exist yet (404).
 * MontageHero will use sprite if available, otherwise fall back to client stitching.
 */
async function preloadObservationImages(observation: WellPage_PastObservation) {
  if (observation.type !== 'plate_imaging' || !observation.montage) return

  const preloadTasks: Promise<unknown>[] = []

  // 1. Preload server-stitched sprite image (highest priority for pan/zoom mode)
  // Always attempt this if URL exists - it will gracefully handle 404/403
  preloadTasks.push(preloadSpriteImage(observation))

  // 2. ALSO preload large images in parallel (for client-side stitching fallback)
  // This handles the case where sprite URL exists in API but file isn't backfilled yet.
  // MontageHero will use these if sprite validation fails.
  if (observation.montage.largeImages && observation.montage.largeImages.length > 0) {
    const warmedUrls = observation.montage.largeImages.map(img =>
      warmImageURL(img.imageUrl),
    )
    preloadTasks.push(
      Promise.allSettled(warmedUrls.map(src => fetch(src, { cache: 'force-cache' }))),
    )
  }

  // 3. Also preload regular montage images (for non-stitched grid view)
  if (observation.montage.images && observation.montage.images.length > 0) {
    const imagesToPreload = observation.montage.images.slice(0, 6)
    for (const image of imagesToPreload) {
      const img = new Image()
      img.src = warmImageURL(image.imageUrl)
    }
  }

  // Wait for sprite and large images to complete (in parallel)
  await Promise.allSettled(preloadTasks)
}

/**
 * Hook to preload adjacent wells for instantaneous navigation
 * Uses adaptive timing via enabled flag - starts when current page images finish loading
 */
export function usePreloadAdjacentWells(
  plateID: string,
  currentWell: string,
  nextWellFor: (dir: LateralNavDirection) => string,
  imagesLoaded: boolean,
) {
  const queryClient = useQueryClient()
  const client = useBackendClient()

  // Calculate which wells to prefetch
  const adjacentWells = useMemo(() => {
    const wells = new Set<string>()

    // Add wells in all 4 directions
    const directions = [
      LateralNavDirection.UP,
      LateralNavDirection.DOWN,
      LateralNavDirection.LEFT,
      LateralNavDirection.RIGHT,
    ]

    for (const dir of directions) {
      const nextWell = nextWellFor(dir)
      if (nextWell !== currentWell) {
        wells.add(nextWell)
      }
    }

    return Array.from(wells)
  }, [currentWell, nextWellFor])

  // Prefetch adjacent wells APIs first (fast, enables responsive UI)
  useEffect(() => {
    if (!imagesLoaded) return // Wait until current page images finish

    let cancelled = false

    const prefetchAPIs = async () => {
      if (cancelled) return

      // Prefetch all adjacent well APIs in parallel (fast ~3s each)
      await Promise.allSettled(
        adjacentWells.map(async well => {
          const cached = queryClient.getQueryData([QUERY_KEYS.WELL_PAGE, plateID, well])
          if (cached) return

          await queryClient.prefetchQuery({
            queryKey: [QUERY_KEYS.WELL_PAGE, plateID, well],
            queryFn: async () =>
              await client.wellPage.getData({
                plate_query_id: plateID,
                well: well,
              }),
            staleTime: 5 * 60 * 1000,
          })
        }),
      )
    }

    prefetchAPIs().catch(() => {
      // Silently fail - prefetching is best-effort optimization
    })

    return () => {
      cancelled = true
    }
    // queryClient and client.wellPage come from stable hooks; including them keeps deps explicit.
  }, [adjacentWells, plateID, imagesLoaded, queryClient, client.wellPage])

  // Then preload images after APIs complete (slow ~10s+ for 16 images)
  useEffect(() => {
    if (!imagesLoaded) return

    let cancelled = false

    const preloadImages = async () => {
      if (cancelled) return

      // Get cached API data for each well
      const results = await Promise.allSettled(
        adjacentWells.map(async well => {
          return queryClient.getQueryData([QUERY_KEYS.WELL_PAGE, plateID, well])
        }),
      )

      if (cancelled) return

      // Preload images for each well's latest observation
      // Sequential per well to avoid overwhelming bandwidth
      for (const result of results) {
        if (cancelled) return

        if (result.status === 'fulfilled' && result.value) {
          const wellData = result.value as WellPageResponse
          const observations = wellData.culture?.observationHistory || []
          const observation =
            observations.length > 0 ? observations[observations.length - 1] : null

          if (observation) {
            await preloadObservationImages(observation)
          }
        }
      }
    }

    // Small delay to let API prefetches complete first
    const timer = setTimeout(() => {
      preloadImages().catch(() => {
        // Silently fail - best-effort optimization
      })
    }, 1000)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [adjacentWells, plateID, imagesLoaded, queryClient])
}

/**
 * Hook to preload images for adjacent observations (prev/next in timeline)
 * HIGH PRIORITY - Users navigate through time more frequently than between wells
 * Warms browser HTTP cache by fetching images without creating blob URLs
 * When user navigates, useLargeImageBlobUrls will fetch from cache and create blob URLs
 */
export function usePreloadAdjacentObservations(
  observations: WellPage_PastObservation[],
  currentIndex: number,
  imagesLoaded: boolean,
) {
  useEffect(() => {
    if (!imagesLoaded || observations.length === 0) return

    let cancelled = false

    const preloadObservations = async () => {
      if (cancelled) return

      const adjacentObservations: WellPage_PastObservation[] = []

      // Preload previous observation
      if (currentIndex > 0) {
        const prevObs = observations[currentIndex - 1]
        if (prevObs?.type === 'plate_imaging' && prevObs.montage) {
          adjacentObservations.push(prevObs)
        }
      }

      // Preload next observation
      if (currentIndex < observations.length - 1) {
        const nextObs = observations[currentIndex + 1]
        if (nextObs?.type === 'plate_imaging' && nextObs.montage) {
          adjacentObservations.push(nextObs)
        }
      }

      // Preload images in parallel (observations are high priority)
      // Start immediately when current page images finish (no delay)
      await Promise.all(adjacentObservations.map(obs => preloadObservationImages(obs)))
    }

    preloadObservations().catch(() => {
      // Silently fail - preloading is best-effort optimization
    })

    return () => {
      cancelled = true
    }
  }, [observations, currentIndex, imagesLoaded])
}

/**
 * Hook to prefetch a specific well on hover (for minimap/compass navigation).
 * Also triggers sprite image preloading after API data is fetched.
 *
 * Returns a synchronous void function to match the expected `onHoverWell` prop type.
 * Async prefetch logic runs in a fire-and-forget IIFE with error handling.
 */
export function usePrefetchWellOnHover(plateID: string): (well: string) => void {
  const queryClient = useQueryClient()
  const client = useBackendClient()

  return (well: string) => {
    // Wrap async logic in IIFE to maintain synchronous return type
    // and ensure errors don't surface as unhandled promise rejections
    ;(async () => {
      try {
        // Check if already cached
        const cached = queryClient.getQueryData<WellPageResponse>([
          QUERY_KEYS.WELL_PAGE,
          plateID,
          well,
        ])

        if (cached) {
          // Data is cached, preload sprite image in background
          const observations = cached.culture?.observationHistory || []
          const latestObs =
            observations.length > 0 ? observations[observations.length - 1] : null
          if (latestObs) {
            await preloadSpriteImage(latestObs)
          }
          return
        }

        // Prefetch immediately on hover
        const data = await queryClient.fetchQuery({
          queryKey: [QUERY_KEYS.WELL_PAGE, plateID, well],
          queryFn: async () =>
            await client.wellPage.getData({
              plate_query_id: plateID,
              well: well,
            }),
          staleTime: 5 * 60 * 1000, // 5 minutes
        })

        // After API data is fetched, preload sprite image in background
        const observations = data?.culture?.observationHistory || []
        const latestObs =
          observations.length > 0 ? observations[observations.length - 1] : null
        if (latestObs) {
          await preloadSpriteImage(latestObs)
        }
      } catch {
        // Silently fail - prefetching is best-effort optimization
      }
    })()
  }
}
